import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getProviderDeviceExpandedData } from '../services/providerDeviceService';
import { formatExpandedData } from '../components/ClinicEquipment/mappers/dataMapper';
import providerService from '../services/providerService';

/**
 * Custom hook for fetching and caching expanded device data.
 *
 * Uses `queryClient.fetchQuery` to fetch-or-cache expanded data for a single
 * device row, then updates the accumulated devices ref so subsequent renders
 * can access the expanded fields without re-fetching.
 *
 * @param {Object} options
 * @param {Object} options.labels - Label map from rendering fields
 * @param {React.MutableRefObject<Array>} options.loanerDataRef - Ref to accumulated devices
 * @param {Function} options.updateDeviceInRef - Callback to merge expanded data into the ref
 * @returns {{ getExpandedData: (assetId: string) => Promise<Object>, invalidateExpandedData: (assetId: string) => void }}
 */
export const useProviderDevice = ({
  labels,
  loanerDataRef,
  updateDeviceInRef,
}) => {
  const { providerId } = providerService.getProviderDetails();
  const queryClient = useQueryClient();

  const getExpandedData = useCallback(
    async (assetId) => {
      const formattedExpandedData = await queryClient.fetchQuery({
        queryKey: ['providerDeviceExpanded', providerId, assetId],
        queryFn: async () => {
          const expandedData = await getProviderDeviceExpandedData(
            providerId,
            assetId,
          );
          const loanerItem = loanerDataRef.current.find(
            (loaner) => loaner.id === assetId,
          );
          return formatExpandedData(
            {
              ...expandedData,
              loanerStatus: loanerItem?.loanerStatus,
            },
            labels,
          );
        },
        staleTime: 5 * 60 * 1000,
      });

      updateDeviceInRef(assetId, formattedExpandedData);
      return formattedExpandedData;
    },
    [
      labels,
      providerId,
      updateDeviceInRef,
      queryClient,
      loanerDataRef,
    ],
  );

  /** Removes cached expanded data for a device so the next expand re-fetches. */
  const invalidateExpandedData = useCallback(
    (assetId) => {
      queryClient.removeQueries({
        queryKey: ['providerDeviceExpanded', providerId, assetId],
      });
    },
    [queryClient, providerId],
  );

  return { getExpandedData, invalidateExpandedData };
};
